import type { CatalogDashboard } from "@app/api/trpc/routers/catalog";
import type { AttentionItem } from "@app/vue/components/Dashboard/AttentionItems";

type SoldProduct = CatalogDashboard["products"][number];

export type Quadrant = "star" | "plowhorse" | "puzzle" | "dog";

export type EngineeredItem = {
  id: number;
  name: string;
  quantity: number;
  /** Net price (without VAT) minus plate cost, per portion. */
  unitMargin: number;
  marginPct: number;
  quadrant: Quadrant;
};

export type MenuEngineering = {
  items: EngineeredItem[];
  /** Sales-weighted average unit margin: the line between high and low margin. */
  avgMargin: number;
  /** 70 % of an equal share of portions sold: the line between popular and not. */
  popularityLine: number;
  /** Products sold in the period that have no recipe, so no margin. */
  uncosted: number;
};

/**
 * Menu engineering (Kasavana & Smith): each costed dish sold in the period
 * is popular when it sells at least 70 % of an equal share, and profitable
 * when its unit margin is at least the sales-weighted average.
 *   star = popular + profitable, plowhorse = popular only,
 *   puzzle = profitable only, dog = neither.
 * Needs at least two costed dishes to compare.
 */
export function engineerMenu(products: SoldProduct[]): MenuEngineering {
  const costed = products.filter((p) => p.unit_cost != null && p.quantity > 0);
  const uncosted = products.filter((p) => p.quantity > 0).length - costed.length;
  if (costed.length < 2) return { items: [], avgMargin: 0, popularityLine: 0, uncosted };

  const rows = costed.map((p) => {
    const netPrice = p.revenue_net / p.quantity;
    const unitMargin = netPrice - (p.unit_cost ?? 0);
    return { p, unitMargin, marginPct: netPrice > 0 ? (unitMargin / netPrice) * 100 : 0 };
  });
  const totalQty = rows.reduce((s, r) => s + r.p.quantity, 0);
  const avgMargin = rows.reduce((s, r) => s + r.unitMargin * r.p.quantity, 0) / totalQty;
  const popularityLine = (totalQty / rows.length) * 0.7;

  const items = rows.map(({ p, unitMargin, marginPct }) => {
    const popular = p.quantity >= popularityLine;
    const profitable = unitMargin >= avgMargin;
    const quadrant: Quadrant = popular ? (profitable ? "star" : "plowhorse") : profitable ? "puzzle" : "dog";
    return { id: p.id, name: p.name, quantity: p.quantity, unitMargin, marginPct, quadrant };
  });
  return { items, avgMargin, popularityLine, uncosted };
}

/** Gross margin % over the sold products that have a recipe, and the share of sales they make up. */
export function menuMargin(products: SoldProduct[]) {
  const costed = products.filter((p) => p.unit_cost != null);
  const net = costed.reduce((s, p) => s + p.revenue_net, 0);
  const cost = costed.reduce((s, p) => s + (p.unit_cost ?? 0) * p.quantity, 0);
  const allNet = products.reduce((s, p) => s + p.revenue_net, 0);
  return {
    marginPct: net > 0 ? ((net - cost) / net) * 100 : null,
    costedShare: allNet > 0 ? (net / allNet) * 100 : null,
  };
}

/**
 * How complete the menu is: the average share of sellable products that are
 * in a category, priced, have a photo and — when the shop tracks stock — a
 * recipe. Null with no sellable products.
 */
export function healthScore(setup: CatalogDashboard["setup"]) {
  if (setup.sellable === 0) return null;
  const parts = [setup.with_category, setup.with_price, setup.with_image];
  if (setup.tracks_stock) parts.push(setup.with_recipe);
  return (parts.reduce((s, n) => s + n / setup.sellable, 0) / parts.length) * 100;
}

const PRODUCTS = "/app/catalog_editor/products";

/** What to fix in the catalog, most urgent first. */
export function catalogAttention(d: CatalogDashboard, t: (k: string) => string): AttentionItem[] {
  const items: AttentionItem[] = [];
  const n = (k: string, count: number) => t(k).replace("{count}", String(count));
  const g = d.gaps;
  if (g.no_price.count > 0) {
    items.push({ icon: "sell", tone: "error", to: PRODUCTS, text: n("catalog.overview.todo_no_price", g.no_price.count), action: t("catalog.overview.action_set_prices") });
  }
  if (g.no_category.count > 0) {
    items.push({ icon: "folder_off", tone: "warning", to: PRODUCTS, text: n("catalog.overview.todo_no_category", g.no_category.count), action: t("catalog.overview.action_assign") });
  }
  if (g.services_without_categories.count > 0) {
    items.push({
      icon: "category", tone: "warning", to: "/app/catalog_editor/services",
      text: n("catalog.overview.todo_service_no_category", g.services_without_categories.count),
      action: t("catalog.overview.action_assign"),
    });
  }
  if (g.out_of_stock.count > 0) {
    items.push({ icon: "remove_shopping_cart", tone: "warning", to: PRODUCTS, text: n("catalog.overview.todo_out_of_stock", g.out_of_stock.count), action: t("catalog.overview.action_review") });
  }
  if (g.empty_categories.count > 0) {
    items.push({
      icon: "folder_open", tone: "info", to: "/app/catalog_editor/categories",
      text: n("catalog.overview.todo_empty_categories", g.empty_categories.count),
      action: t("catalog.overview.action_review"),
    });
  }
  if (d.setup.tracks_stock && g.no_recipe.count > 0) {
    items.push({ icon: "receipt_long", tone: "info", to: PRODUCTS, text: n("catalog.overview.todo_no_recipe", g.no_recipe.count), action: t("catalog.overview.action_add_recipes") });
  }
  if (g.unsold.count > 0 && d.sales.items_sold > 0) {
    items.push({
      icon: "trending_down", tone: "info", to: PRODUCTS,
      text: n("catalog.overview.todo_unsold", g.unsold.count).replace("{days}", String(d.days)),
      action: t("catalog.overview.action_review"),
    });
  }
  return items;
}
